import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  completeSignupRequest,
  resendSignupOtpRequest,
  signupRequest,
  verifyEmailOtpRequest,
  verifyPhoneSignupRequest,
  type ResendSignupOtpPayload,
  type SetPasswordPayload,
  type SignupPayload,
  type VerifyEmailOtpPayload,
  type VerifyPhoneSignupPayload,
} from '@/features/auth/api/authApi';
import {
  applySession,
  getApiMessage,
  getSessionId,
  phoneOtpNavState,
} from '@/features/auth/api/session';
import { getErrorMessage } from '@/shared/api/errors';
import { useAppDispatch } from '@/shared/store/hooks';
import { setCredentials } from '@/shared/store/slices/authSlice';

export function useSignupMutation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: SignupPayload) => signupRequest(payload),
    onSuccess: (data, payload) => {
      const sessionId = getSessionId(data);
      if (sessionId) localStorage.setItem('SESSION_ID_TWO', sessionId);
      localStorage.setItem('otp_type', 'Email');
      localStorage.setItem('role', payload.accountType);
      localStorage.setItem('user_mobile', payload.mobileNumber || '');
      localStorage.setItem(
        'user_mobile_country',
        payload.mobileCountryCode || '',
      );
      localStorage.setItem('user_email', payload.email || '');

      toast.success(getApiMessage(data, 'Account created — verify your email'));
      navigate('/otp-verify', {
        state: {
          type: 'Email',
          email: payload.email,
          mobileNumber: payload.mobileNumber,
        },
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Signup failed'));
    },
  });
}

export function useVerifyEmailOtpMutation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: VerifyEmailOtpPayload) =>
      verifyEmailOtpRequest(payload),
    onSuccess: () => {
      localStorage.setItem('otp_type', 'Phone');
      toast.success('Email verified — check your phone for OTP');
      navigate('/otp-verify', { replace: true, state: phoneOtpNavState() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Invalid email OTP'));
    },
  });
}

export function useVerifyPhoneSignupMutation() {
  return useMutation({
    mutationFn: (payload: VerifyPhoneSignupPayload) =>
      verifyPhoneSignupRequest(payload),
    onSuccess: (data) => {
      const sessionId = getSessionId(data);
      if (sessionId) localStorage.setItem('SESSION_ID', sessionId);
      localStorage.removeItem('SESSION_ID_TWO');
      toast.success('Phone verified');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Invalid phone OTP'));
    },
  });
}

export function useResendSignupOtpMutation() {
  return useMutation({
    mutationFn: (payload: ResendSignupOtpPayload) =>
      resendSignupOtpRequest(payload),
    onSuccess: (data) => {
      toast.success(getApiMessage(data, 'OTP resent'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to resend OTP'));
    },
  });
}

export function useCompleteSignupMutation() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: SetPasswordPayload) =>
      completeSignupRequest(payload),
    onSuccess: (data) => {
      const user = applySession(data);
      if (user) dispatch(setCredentials({ user }));
      localStorage.removeItem('SESSION_ID');
      navigate('/dashboard', { replace: true });
      window.setTimeout(() => {
        toast.success('Account created');
      }, 100);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to set password'));
    },
  });
}
